import moment from "moment";

import {
  ActivityCategory,
  ActivityLogEntry,
  ActivitySegment,
} from "./activityLog.types";

const n = (text: string): ActivitySegment => ({ text });
const b = (text: string): ActivitySegment => ({ text, bold: true });

export const AUTOMATIC_NAME = "ProCharted (automatic)";

export const ACTIVITY_FILTERS: {
  key: "all" | ActivityCategory;
  label: string;
}[] = [
  { key: "all", label: "All activity" },
  { key: "Client", label: "Clients" },
  { key: "Work", label: "Work" },
  { key: "File", label: "Files" },
  { key: "Status", label: "Status" },
  { key: "Comment", label: "Comments" },
  // LOG-001 (QA batch 17): two-step sign-in resets / firm switch.
  { key: "Team", label: "Team" },
  // Client request lists (Mg-232).
  { key: "Request", label: "Client requests" },
];

const VERBS: Record<string, string> = {
  created: "created",
  edited: "edited",
  archived: "archived",
  unarchived: "restored",
  deleted: "deleted",
};

/**
 * The sentence after the person's name, e.g. "changed status of [2025 Tax
 * Return] to [In Progress]". `inWork` = shown on that work item's Log tab, so
 * the work item's own name is left out ("changed status to In Progress").
 */
export const activitySentence = (
  entry: ActivityLogEntry,
  inWork = false,
): ActivitySegment[] => {
  const work = entry.workName || "a work item";
  const client = entry.customerName || "a client";
  const detail = entry.detail || "";
  const target = entry.workId ? work : client;

  switch (entry.category) {
    case "Status":
      return inWork
        ? [n("changed status to "), b(detail || "a new status")]
        : [
            n("changed status of "),
            b(work),
            n(" to "),
            b(detail || "a new status"),
          ];
    case "Comment": {
      const verb =
        entry.action === "added"
          ? "added"
          : VERBS[entry.action] || entry.action;
      return inWork
        ? [n(`${verb} a comment`)]
        : [n(`${verb} a comment on `), b(target)];
    }
    case "File":
      // Documents: "moved X to Y", "created folder X", "shared with client: X"…
      if (entry.action === "edited") {
        return inWork
          ? [n(detail || "changed a file")]
          : [n(detail || "changed a file"), n(" ("), b(target), n(")")];
      }
      if (entry.action === "restored") {
        return inWork
          ? [n("restored file "), b(detail)]
          : [n("restored file "), b(detail), n(" of "), b(target)];
      }
      if (entry.action === "deleted") {
        return inWork
          ? [n("deleted file "), b(detail)]
          : [n("deleted file "), b(detail), n(" from "), b(target)];
      }
      return inWork
        ? [n("uploaded "), b(detail)]
        : [n("uploaded "), b(detail), n(" to "), b(target)];
    case "Client":
      return [n(`${VERBS[entry.action] || entry.action} client `), b(client)];
    case "Team":
      // Two-step sign-in (Roadmap 1845).
      if (entry.action === "two_step_reset")
        return [n("reset two-step sign-in for "), b(detail || "a team member")];
      if (entry.action === "two_step_required")
        return [
          n("turned on "),
          b("Require two-step sign-in"),
          n(" for everyone"),
        ];
      if (entry.action === "two_step_not_required")
        return [
          n("turned off "),
          b("Require two-step sign-in"),
          n(" for everyone"),
        ];
      return [n(detail || "changed a team setting")];
    case "Request": {
      // Client request lists (Mg-232): "sent client request list to ABC Corp: October documents",
      // "reminded client to finish October documents (automatic reminder 2 of 6)", "completed …".
      const verb =
        entry.action === "created" ? "made a" : entry.action || "changed a";
      const text = `${verb} ${detail || "client request list"}`;
      return inWork ? [n(text)] : [n(text), n(" ("), b(target), n(")")];
    }
    case "Work":
    default:
      return inWork
        ? [n(`${VERBS[entry.action] || entry.action} this work item`)]
        : [n(`${VERBS[entry.action] || entry.action} work item `), b(work)];
  }
};

/** "Today", "Yesterday" or "Jun 24, 2026", in the viewer's time zone. */
export const activityDayLabel = (createdAt: string, now = moment()) => {
  const day = moment(createdAt).startOf("day");
  const today = now.clone().startOf("day");
  if (day.isSame(today)) return "Today";
  if (day.isSame(today.clone().subtract(1, "day"))) return "Yesterday";
  return day.format("MMM D, YYYY");
};

/** "Oct 19, 2026 · 2:42 AM", in the viewer's time zone. */
export const activityTimeLabel = (createdAt: string) =>
  moment(createdAt).format("MMM D, YYYY · h:mm A");

/** Entries (newest first) grouped by day, keeping their order. */
export const groupActivityByDay = (
  entries: ActivityLogEntry[],
  now = moment(),
) => {
  const groups: { day: string; entries: ActivityLogEntry[] }[] = [];
  entries.forEach((entry) => {
    const day = activityDayLabel(entry.createdAt, now);
    const last = groups[groups.length - 1];
    if (last && last.day === day) last.entries.push(entry);
    else groups.push({ day, entries: [entry] });
  });
  return groups;
};

export const activityInitials = (name: string) => {
  if (name === AUTOMATIC_NAME) return "PC";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return (
    (parts[0][0] || "") + (parts.length > 1 ? parts[parts.length - 1][0] : "")
  ).toUpperCase();
};

/** Where clicking an entry goes (null = nothing to open, e.g. deleted). */
export const activityLink = (entry: ActivityLogEntry): string | null => {
  if (entry.workId && entry.workExists) return `/work/${entry.workId}`;
  if (entry.businessContactId)
    return `/organization/${entry.businessContactId}`;
  if (entry.customerId && entry.contactId)
    return `/contact/${entry.customerId}?contactId=${entry.contactId}`;
  return null;
};
